/**
 * 需要想定 draft の保存キュー (save 並行性 / 楽観ロック) を所有する headless hook。
 *
 * useForecastDraftEditor (god-hook) から「最高 risk の並行性クラスタ」を切り出した
 * deep module (architecture review C4)。この 1 箇所に集約するのは:
 *   - executeSaveAll の直列化 (saveRunRef): double-submit / 自己 ABORTED の再発防止
 *   - editRowsRef の sync-snapshot hack: イベントハンドラから直近コミット済みの
 *     editRows を同期読みし、キューされた次の run が stale な baseline / draftUpdatedAt
 *     を読むのを防ぐ
 *   - saveStates の楽観ロック競合遷移 (ABORTED → conflict / それ以外 → error)
 * これらは page を render せず renderHook で単体テストできる (editRows / setEditRows /
 * 保存文脈を props で受け、保存 RPC は useSaveDemandForecastDraft 経由で Connect
 * Transport 差し替えでテストする)。
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  DemandForecastSaveErrorReason,
  DemandForecastSaveErrorReasonSchema,
  SaveDemandForecastDraftRequestSchema,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import { readAppErrorInfo } from '~/libs/appErrorInfo'
import { now as serverNow } from '~/models/clock'
import {
  retailerSlotLists,
  saveTargetOf,
} from '~/pages/DemandForecastDaily/models/editUnits'
import {
  fieldsSnapshotOf,
  isRowDirty,
} from '~/pages/DemandForecastDaily/models/forecastDirtyState'
import { resolveSaveDetailedErrorMessage } from '~/pages/DemandForecastDaily/models/serverError'
import {
  bgTotalBreakdownSlots,
  komaToProtoSlots,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import {
  type SlotValueLimitViolation,
  collectNegativeKomas,
  findSlotValueLimitViolationsFromRetailerSlots,
} from '~/pages/DemandForecastDaily/models/slotValueLimit'
import type {
  DraftSaveAcknowledgement,
  RetailerEditRow,
  RetailerFieldsSnapshot,
  SaveState,
  UnattributedEditRow,
} from '~/pages/DemandForecastDaily/models/types'

import type { AdoptedReferenceColumnToken } from './useAdoptReferenceColumn'
import { useSaveDemandForecastDraft } from './useSaveDemandForecastDraft'

/** saveStates 内で帰属不明内訳 (scope 4) を表す安定した key。 */
export const UNATTRIBUTED_SAVE_STATE_KEY = '__unattributed__'

type UseDraftSaveQueueParams = {
  unattributedRow?: UnattributedEditRow
  setUnattributedRow?: Dispatch<SetStateAction<UnattributedEditRow | undefined>>
  editRows: Record<string, RetailerEditRow>
  /** 現在の page にある行だけ。上限集計には保持した旧行を含めない。 */
  activeEditRows?: Record<string, RetailerEditRow>
  setEditRows: Dispatch<SetStateAction<Record<string, RetailerEditRow>>>
  caseId: string | undefined
  selectedAreaCode: AreaCode | undefined
  section: WorkflowSection
  targetDate: ProtoDate | undefined
  canMutateNow: () => boolean
  /** GC 済みコマ数。上限超過・負の値の判定は GC 以降のコマ (koma > gcKoma) だけ。 */
  gcKoma: number
  /**
   * 参照列「採用」undo の Decision 3 連携 (任意)。保存開始時点の採用エントリ捕捉と
   * 保存成功時の identity-guarded クリアを useAdoptReferenceColumn へ委譲する。
   * 未指定なら save queue は採用 state を一切触らない (並行性の単体テストは
   * この連携なしで完結する)。
   */
  onSaveStart?: (retailerBusinessCd: string) => AdoptedReferenceColumnToken
  onSaved?: (
    retailerBusinessCd: string,
    entryAtSaveStart: AdoptedReferenceColumnToken,
    acknowledgement?: DraftSaveAcknowledgement,
  ) => void
}

/**
 * `executeSaveAll` の結果。
 * - `limitRejected`: 上限超過 (AC-DFC-EDIT-004) で保存 RPC を 1 件も発行せずに拒否した
 *   (editRows は触れない = 編集中の値が残る)。`violations` は空でない。
 * - `notSent`: 過去日 gate・対象 ID の欠けで保存 RPC を発行しなかった (保存失敗扱い)。
 * - `sent`: 保存を発行した。`ok` は全て成功した時だけ true。`negativeKomas` は保存に成功した小売の値にある GC 以降の負のコマ
 *   (コマ順・重複なし、AC-DFC-EDIT-005 の警告対象)。
 */
export type SaveAllResult =
  | { kind: 'limitRejected'; violations: SlotValueLimitViolation[] }
  | { kind: 'notSent' }
  | { kind: 'sent'; ok: boolean; negativeKomas: number[] }

/**
 * A single editor-owned view of the rows to persist.  Aggregate preview capture
 * will construct this at the same boundary that it adopts a server result; the
 * save queue deliberately receives rows only and knows nothing about intents or
 * allocation.
 */
export type EditorSaveSnapshot = Readonly<{
  editRows: Record<string, RetailerEditRow>
  activeEditRows: Record<string, RetailerEditRow>
  unattributedRow: UnattributedEditRow | undefined
}>

const NOT_SAVED: SaveAllResult = { kind: 'notSent' }

/**
 * These rejections mean the selected retail/leaf mode changed after the
 * editor loaded. They enter the existing conflict UI state so the caller
 * prompts the user to reload latest rather than leaving an inactive row's
 * error hidden from the current tab.
 */
function requiresReloadLatest(err: ConnectError): boolean {
  if (err.code === Code.Aborted || err.code === Code.AlreadyExists) return true
  if (err.code !== Code.FailedPrecondition) return false
  const reason = readAppErrorInfo(
    err,
    DemandForecastSaveErrorReasonSchema,
  )?.reason
  return (
    reason === DemandForecastSaveErrorReason.LEAF_TARGET_NOT_OPEN ||
    reason === DemandForecastSaveErrorReason.RETAILER_FORECAST_BY_GROUP
  )
}

export type UseDraftSaveQueueResult = {
  /** 保存フロー状態 (小売単位、設計書 §5)。 */
  saveStates: Record<string, SaveState>
  /**
   * 保存失敗 (saveStates[cd] === 'error') の原因別文言 (小売単位)。reason を
   * 解決できなかった場合は entry が無い (呼び出し元は code ベース fallback
   * 文言を表示する)。
   */
  saveErrorMessages: Record<string, string | undefined>
  /** 最終保存時刻 (表示用、設計書 §4「最終保存 M/D HH:mm」表示)。 */
  lastSavedAt: Date | undefined
  /** 保存中 (saving) の小売が 1 つでもあるか。 */
  hasInFlight: boolean
  /** 楽観ロック衝突 (conflict) の小売が 1 つでもあるか (AC-09)。 */
  anyConflict: boolean
  /**
   * 「保存」ボタン (AC-038/039/040): dirty な全小売分の SaveDemandForecastDraft を
   * 呼ぶ。全て成功した場合のみ `{ kind: 'sent', ok: true }` を返す (失敗した小売のみ
   * dirty 残留、AC-042)。画面の全事業者の編集中の値が上限を超える時
   * (AC-DFC-EDIT-004) は 1 件も送らず `{ kind: 'limitRejected' }` を返す。保存ボタンと
   * 「保存して移動」の両方がこの入口を通る。
   * 先行 run があればその完了を待ってから残 dirty を再計算する形に直列化する。
   */
  executeSaveAll: (snapshot?: EditorSaveSnapshot) => Promise<SaveAllResult>
  /**
   * 破棄 (AC-07) 時の saveState クリア。対象を省略すると saving 以外の全 entry、
   * 指定すると対象 entry だけを削除する (editRows の baseline 復元は呼び出し元が行う)。
   */
  clearSaveStatesForDiscard: (retailerBusinessCds?: readonly string[]) => void
  /**
   * 楽観ロック衝突解消 (AC-09) 時の saveState 遷移: 対象小売を idle に戻す
   * (editRows の baseline 復元・refetch は呼び出し元が行う)。
   */
  markResolved: (retailerBusinessCds: string[]) => void
}

export function useDraftSaveQueue({
  unattributedRow,
  setUnattributedRow,
  editRows,
  activeEditRows = editRows,
  setEditRows,
  caseId,
  selectedAreaCode,
  section,
  targetDate,
  canMutateNow,
  gcKoma,
  onSaveStart,
  onSaved,
}: UseDraftSaveQueueParams): UseDraftSaveQueueResult {
  const { t } = useTranslation()
  const { mutateAsync: saveAsync } = useSaveDemandForecastDraft()

  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({})
  const [saveErrorMessages, setSaveErrorMessages] = useState<
    Record<string, string | undefined>
  >({})
  const [lastSavedAt, setLastSavedAt] = useState<Date | undefined>(undefined)

  // editRows の「常に最新」な同期スナップショット。React の setState updater
  // (`setEditRows((prev) => { snapshot = prev; return prev })`) で現在値を同期的に
  // 読もうとすると、updater の呼び出しタイミングが React のバッチング/eager-state
  // 最適化に依存し信頼できない (= 稀に stale な `{}` を読んでしまう)。ref は
  // レンダー毎に代入されるため、イベントハンドラから呼ばれる executeSaveAll は
  // 常に直近コミット済みの editRows を読める (KomaGrid.tsx の startEditRef と同じ
  // パターン)。
  const editRowsRef = useRef(editRows)
  editRowsRef.current = editRows
  const activeEditRowsRef = useRef(activeEditRows)
  activeEditRowsRef.current = activeEditRows
  const unattributedRowRef = useRef(unattributedRow)
  unattributedRowRef.current = unattributedRow

  // ── executeSaveAll (AC-038/039/040/042) ──────────────────────────
  //
  // dirty な全小売分の SaveDemandForecastDraft を並行して呼ぶ (非 atomic、§4
  // 主要意思決定「保存モデル」)。個別の失敗は失敗した小売のみ dirty 残留し、
  // 全体としては `ok: false` を返す (呼び出し元は「離脱せず留まる」等の判断に使う)。

  // 実行中の run を保持する ref (review 指摘 PR #3199: executeSaveAll 直列化)。
  // 保存応答まで dirty が残る設計 (baseline は応答で更新) のため、「保存」
  // 押下直後に離脱ダイアログで「保存して移動」を選ぶと、同じ expectedUpdatedAt
  // (= row.draftUpdatedAt は run1 応答まで未更新) で2本目の
  // SaveDemandForecastDraft が発行されてしまう (run1 成功 → run2 が自己
  // ABORTED)。executeSaveAllOnce を直接公開せず、下の executeSaveAll wrapper で
  // 先行 run の完了を待ってから残 dirty を再計算する形に直列化する。
  const saveRunRef = useRef<Promise<SaveAllResult> | null>(null)

  const executeSaveAllOnce = useCallback(
    async (snapshot?: EditorSaveSnapshot): Promise<SaveAllResult> => {
      // 過去日 gate: SaveDemandForecastDraft を発行せず `ok: false` を返す (書込失敗扱い、
      // dirty は残留)。日跨ぎ後の残存保存経路 (保存ボタン / 「保存して移動」) を封鎖する。
      if (!canMutateNow()) return NOT_SAVED
      if (
        selectedAreaCode === undefined ||
        targetDate === undefined ||
        caseId === undefined
      )
        return NOT_SAVED

      const rowsAtSaveStart = snapshot?.editRows ?? editRowsRef.current
      const activeRowsAtSaveStart =
        snapshot?.activeEditRows ?? activeEditRowsRef.current
      const dirtyEntries = Object.entries(rowsAtSaveStart).filter(([, row]) =>
        isRowDirty(row),
      )
      const unattributedAtSaveStart =
        snapshot === undefined
          ? unattributedRowRef.current
          : snapshot.unattributedRow
      const unattributedDirty =
        unattributedAtSaveStart !== undefined &&
        isRowDirty(unattributedAtSaveStart)
      if (dirtyEntries.length === 0 && !unattributedDirty) {
        return { kind: 'sent', ok: true, negativeKomas: [] }
      }

      // AC-DFC-EDIT-004: 上限超過 (1 コマ・BG 全体の合計) は 1 件も送らずに拒否する。
      // 保存は小売ごとの並行 RPC でトランザクションが無く、保存後の BG 全体を
      // 知っているのは画面だけなので、判定対象は画面の全事業者の編集中の値。
      // 編集中の値はそのまま残る (editRows には触れない)。
      // グループ別の小売は葉の元値を十進で足して判定する。保持した旧行は保存対象として
      // 残すが、現在の page の小売値・BG 値には含めない。
      const violations = findSlotValueLimitViolationsFromRetailerSlots(
        retailerSlotLists(activeRowsAtSaveStart),
        gcKoma,
        unattributedAtSaveStart?.forecastSlots,
      )
      if (violations.length > 0) {
        return { kind: 'limitRejected', violations }
      }

      setSaveStates((prev) => {
        const next = { ...prev }
        for (const [retailerBusinessCd] of dirtyEntries)
          next[retailerBusinessCd] = 'saving'
        if (unattributedDirty) next[UNATTRIBUTED_SAVE_STATE_KEY] = 'saving'
        return next
      })

      let allOk = true
      let anySucceeded = false
      const savedForecastSlots: Array<RetailerEditRow['forecastSlots']> = []
      let savedUnattributedForecastSlots:
        | UnattributedEditRow['forecastSlots']
        | undefined
      // この run で成功した小売の baseline/draftUpdatedAt 更新 (executeSaveAll
      // 直列化用、下記 ref 同期で使う)。
      const succeededUpdates: Record<
        string,
        Pick<
          RetailerEditRow,
          'draftUpdatedAt' | 'baseline' | 'carriedOverFromGroups'
        >
      > = {}

      await Promise.all(
        [
          ...dirtyEntries.map(([retailerBusinessCd, row]) => ({
            retailerBusinessCd,
            row,
            unattributed: false as const,
          })),
          ...(unattributedDirty && unattributedAtSaveStart !== undefined
            ? [
                {
                  retailerBusinessCd: undefined,
                  row: unattributedAtSaveStart,
                  unattributed: true as const,
                },
              ]
            : []),
        ].map(async ({ retailerBusinessCd, row, unattributed }) => {
          // 保存開始時点の採用エントリ (参照列「採用」Decision 3、識別トークン)。
          // 採用のたびに新しいオブジェクトを生成するため、参照一致は「同一
          // インスタンスの採用」を厳密に表す。RPC 応答前に別列採用へ切り替えた場合は
          // もちろん、同じ列を解除→再採用した場合 (key は同じでも別インスタンス) も
          // 別物として扱い、この保存成功で新採用の undo state を消さないための識別子。
          // 帰属不明の entry は UNATTRIBUTED_SAVE_STATE_KEY で持つ (集計採用、issue #11461)。
          const adoptKey = retailerBusinessCd ?? UNATTRIBUTED_SAVE_STATE_KEY
          const adoptedEntryAtSaveStart = onSaveStart?.(adoptKey)
          try {
            const request = create(SaveDemandForecastDraftRequestSchema, {
              caseId,
              supportsUnattributed: true,
              areaCode: selectedAreaCode,
              section,
              targetDate,
              retailerBusinessCd: unattributed
                ? undefined
                : row.retailerBusinessCd,
              unattributed,
              ...(unattributed ? {} : saveTargetOf(row.unit)),
              forecastSlots: komaToProtoSlots(row.forecastSlots),
              providerSlots: komaToProtoSlots(row.providerSlots),
              manualEditKomas: Array.from(row.manualEditKomas),
              provider: row.provider,
              providerOther: row.providerOther,
              providerLabel: row.providerLabel,
              expectedUpdatedAt: row.draftUpdatedAt,
            })
            const response = await saveAsync(request)
            anySucceeded = true
            if (unattributed) savedUnattributedForecastSlots = row.forecastSlots
            else savedForecastSlots.push(row.forecastSlots)
            // baseline は「保存を送った時点のスナップショット (row)」に更新する
            // (現在値 = editRows[retailerBusinessCd] は保存中に編集が進んでいる可能性があり、
            // その場合 computeForecastDiff(newBaseline, current) が引き続き dirty を
            // 検出する = 画面状態マトリクス「保存中の編集は応答後も dirty 維持」)。
            const savedBaseline: RetailerFieldsSnapshot = fieldsSnapshotOf(row)
            if (retailerBusinessCd === undefined) {
              const current = unattributedRowRef.current
              if (current !== undefined) {
                unattributedRowRef.current = {
                  ...current,
                  draftUpdatedAt: response.updatedAt,
                  baseline: savedBaseline,
                }
              }
              setUnattributedRow?.((current) =>
                current === undefined
                  ? current
                  : {
                      ...current,
                      draftUpdatedAt: response.updatedAt,
                      baseline: savedBaseline,
                    },
              )
              setSaveStates((prev) => ({
                ...prev,
                [UNATTRIBUTED_SAVE_STATE_KEY]: 'idle',
              }))
            } else {
              succeededUpdates[retailerBusinessCd] = {
                draftUpdatedAt: response.updatedAt,
                baseline: savedBaseline,
                carriedOverFromGroups: false,
              }
              setEditRows((prev) => {
                const current = prev[retailerBusinessCd]
                if (current === undefined) return prev
                return {
                  ...prev,
                  [retailerBusinessCd]: {
                    ...current,
                    draftUpdatedAt: response.updatedAt,
                    baseline: savedBaseline,
                    carriedOverFromGroups: false,
                  },
                }
              })
              setSaveStates((prev) => ({
                ...prev,
                [retailerBusinessCd]: 'idle',
              }))
            }
            setSaveErrorMessages((prev) => ({
              ...prev,
              [retailerBusinessCd === undefined
                ? UNATTRIBUTED_SAVE_STATE_KEY
                : retailerBusinessCd]: undefined,
            }))
            // Decision 3 (参照列「採用」): 保存で確定し、採用中の undo state を
            // クリアする (ボタンは通常「採用」表示へ戻る。取込元 providerLabel
            // 自体は保存済み値としてそのまま残る)。ただし保存 RPC 応答前に別列を
            // 採用した場合はキーが変わっているため、その新採用の undo は消さない
            // (保存開始時点と同一の採用に限ってクリアする)。
            onSaved?.(
              adoptKey,
              adoptedEntryAtSaveStart,
              unattributed
                ? undefined
                : {
                    saveContext: {
                      caseId: request.caseId,
                      areaCode: request.areaCode,
                      section: request.section,
                      targetDate: request.targetDate,
                    },
                    key: retailerBusinessCd,
                    previousExpectedVersion: row.draftUpdatedAt,
                    previousBaseline: row.baseline,
                    previousCarriedOverFromGroups: row.carriedOverFromGroups,
                    savedBaseline,
                    updatedAt: response.updatedAt,
                    carriedOverFromGroups: false,
                  },
            )
          } catch (err) {
            allOk = false
            const isConflict =
              err instanceof ConnectError && requiresReloadLatest(err)
            // AC-09: conflict は未保存変更を保持したまま自動 refetch せず、呼び出し元が
            // reload latest を促して resolveConflict を呼ぶまで待つ。
            setSaveStates((prev) => ({
              ...prev,
              [retailerBusinessCd === undefined
                ? UNATTRIBUTED_SAVE_STATE_KEY
                : retailerBusinessCd]: isConflict ? 'conflict' : 'error',
            }))
            // AC-DFC-007 readonly gate 等の reason 別文言。解決できなければ
            // undefined を保持し、呼び出し元 (KomaCard) が既存の code ベース
            // fallback 文言を表示する。
            setSaveErrorMessages((prev) => ({
              ...prev,
              [retailerBusinessCd === undefined
                ? UNATTRIBUTED_SAVE_STATE_KEY
                : retailerBusinessCd]:
                !isConflict && err instanceof ConnectError
                  ? resolveSaveDetailedErrorMessage(err, t)
                  : undefined,
            }))
          }
        }),
      )

      // executeSaveAll 直列化 (review 指摘 PR #3199) 対応: setEditRows の
      // updater 呼び出しは React のバッチング/コミットタイミングに依存し非同期
      // になりうるため、直後にキューされた run (executeSaveAllOnce) が同一
      // microtask 内で dirty を再計算すると、まだレンダーに反映されていない
      // stale な baseline / draftUpdatedAt を読んでしまう (二重送信・自己
      // ABORTED の再発)。この run が確定させた更新のみを editRowsRef.current に
      // も同期反映する (他の行への並行編集は editRowsRef.current の現在値を
      // 起点にするため上書きしない)。
      if (Object.keys(succeededUpdates).length > 0) {
        const next = { ...editRowsRef.current }
        for (const [retailerBusinessCd, update] of Object.entries(
          succeededUpdates,
        )) {
          const current = next[retailerBusinessCd]
          if (current === undefined) continue
          next[retailerBusinessCd] = { ...current, ...update }
        }
        editRowsRef.current = next
      }

      if (anySucceeded) setLastSavedAt(serverNow())
      // AC-DFC-EDIT-005: 成功した小売とBG合計に含まれる帰属不明の負値を知らせる。
      // 全件失敗時は保存を行っていないため、新たな警告は出さない。
      return {
        kind: 'sent',
        ok: allOk,
        negativeKomas: anySucceeded
          ? collectNegativeKomas(
              bgTotalBreakdownSlots(
                savedForecastSlots,
                savedUnattributedForecastSlots ??
                  (!unattributedDirty
                    ? unattributedAtSaveStart?.forecastSlots
                    : undefined),
              ),
              gcKoma,
            )
          : [],
      }
    },
    [
      gcKoma,
      caseId,
      selectedAreaCode,
      section,
      targetDate,
      saveAsync,
      canMutateNow,
      setEditRows,
      setUnattributedRow,
      onSaveStart,
      onSaved,
      t,
    ],
  )

  // executeSaveAll: 先行 run があればその完了を待って (reject も握りつぶす) から
  // 新しい run を開始する。先行 run 完了後は成功した行の baseline /
  // draftUpdatedAt が更新済みのため、executeSaveAllOnce が読む editRowsRef から
  // 残 dirty (= 保存中に編集された行を含む) が正しく再計算され、二重送信・自己
  // ABORTED は発生しない。await 後に別の run が既に開始されている可能性に
  // 備え、ref が空になるまで待つループにする (JS single-thread の再チェックで
  // 十分)。
  const executeSaveAll = useCallback(
    async (snapshot?: EditorSaveSnapshot): Promise<SaveAllResult> => {
      let waitedForPreviousRun = false
      while (saveRunRef.current !== null) {
        waitedForPreviousRun = true
        await saveRunRef.current.catch(() => undefined)
      }
      // A snapshot is atomic only at its capture boundary. Once a preceding
      // save has completed, its baseline/lock token is newer; re-evaluate the
      // current rows rather than sending the stale captured token again.
      const run = executeSaveAllOnce(
        waitedForPreviousRun ? undefined : snapshot,
      )
      saveRunRef.current = run
      try {
        return await run
      } finally {
        if (saveRunRef.current === run) saveRunRef.current = null
      }
    },
    [executeSaveAllOnce],
  )

  const clearSaveStatesForDiscard = useCallback(
    (retailerBusinessCds?: readonly string[]): void => {
      setSaveStates((prev) => {
        const next = { ...prev }
        for (const retailerBusinessCd of retailerBusinessCds ??
          Object.keys(next)) {
          if (next[retailerBusinessCd] !== 'saving')
            delete next[retailerBusinessCd]
        }
        return next
      })
      setSaveErrorMessages((prev) => {
        if (retailerBusinessCds === undefined) return {}
        const next = { ...prev }
        for (const retailerBusinessCd of retailerBusinessCds)
          delete next[retailerBusinessCd]
        return next
      })
    },
    [],
  )

  const markResolved = useCallback((retailerBusinessCds: string[]): void => {
    if (retailerBusinessCds.length === 0) return
    setSaveStates((prev) => {
      const next = { ...prev }
      for (const retailerBusinessCd of retailerBusinessCds)
        next[retailerBusinessCd] = 'idle'
      return next
    })
    setSaveErrorMessages((prev) => {
      const next = { ...prev }
      for (const retailerBusinessCd of retailerBusinessCds)
        delete next[retailerBusinessCd]
      return next
    })
  }, [])

  const hasInFlight = useMemo(
    () => Object.values(saveStates).some((s) => s === 'saving'),
    [saveStates],
  )

  const anyConflict = useMemo(
    () => Object.values(saveStates).some((s) => s === 'conflict'),
    [saveStates],
  )

  return {
    saveStates,
    saveErrorMessages,
    lastSavedAt,
    hasInFlight,
    anyConflict,
    executeSaveAll,
    clearSaveStatesForDiscard,
    markResolved,
  }
}
